import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useForm, Control } from 'react-hook-form';
import {
  GeneralLedgerSidepanel,
  Popup,
  PermissionEnum,
  GeneralLedgerTransaction as GeneralLedgerTransactionType,
  GeneralLedgerTransactionFormValues,
  ResponseError,
  responseErrorsResolver,
  ajvResolver,
  usePrevious,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { changePanelState } from './store/slices/transaction';
import { GLTransactionTypes } from './types';
import useRequests from './hooks/use-requests';

import cn from './styles.module.css';

const formatFormDate = (date: string) => {
  const dateFormatted = new Date(date);

  dateFormatted.setHours(0);
  dateFormatted.setMinutes(0);
  dateFormatted.setSeconds(0);
  dateFormatted.setMilliseconds(0);

  return dateFormatted;
};

const schema = {
  type: 'object',
  properties: {
    sum: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Amount is required',
    },
    debitAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Debit accounts is required',
    },
    creditAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Credit account is required',
    },
    currencyId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency is required',
    },
    info: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Info is required',
    },
  },
  required: [
    'sum',
    'debitAccountNumber',
    'creditAccountNumber',
    'currencyId',
    'info',
  ],
};

const GeneralLedgerTransaction = ({
  restatement,
}: GLTransactionTypes): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { transactionId } = useParams();
  const { search, pathname } = useLocation();

  const {
    generalLedger: {
      transaction: {
        transaction,
        creditAccountBalance,
        debitAccountBalance,
        accountCategories,
        panelIsOpen,
        creditBalances,
        debitBalances,
        documents,
        documentsPagination,
        document: documentItem,
      },
      list: { selectedTake },
    },
    transactionsDictionaries: {
      activePermissions,
      currencies,
      transactionsTypes,
      baseCurrency,
      currenciesList: dictionariesCurrenciesList,
      overdraftEnabled,
    },
    transactions: { transactionChangeLogsPagination, transactionChangeLogs },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const prevPanelIsOpen = usePrevious(panelIsOpen);

  const defaultFormValues: any = {
    creditAccountId: '',
    creditAccountNumber: '',
    debitAccountId: '',
    debitAccountNumber: '',
    documentNumber: '',
    info: '',
    sum: '',
    transferDate: new Date().toISOString(),
  };

  const {
    control,
    setError,
    watch,
    formState: { errors, dirtyFields },
    setValue,
    clearErrors,
    getValues,
    handleSubmit,
  } = useForm({
    resolver: ajvResolver(schema),
    defaultValues: defaultFormValues,
  });

  const fields = watch();
  const currencyId = watch('currencyId');
  const debitAccountNumber = watch('debitAccountNumber');
  const creditAccountNumber = watch('creditAccountNumber');
  const transferDateValue = watch('transferDate');

  const isDirty = Object.keys(dirtyFields).length !== 0;

  const {
    deletePopupVisible,
    setDeletePopupVisible,
    errorMessage,
    setErrorMessage,
    deleteError,
    availableCurrencies,
    attachedDocumentsErrors,
    uploadedFilesError,
    uploadedDocumentsErrors,
    imgLink,
    documentsIsLoading,
    documentsIsFetching,
    create,
    createIsLoading,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    checkDocumentNameInProgress,
    fileUploadInProgress,
    downloadInProgress,
    downloadIsFetching,
    downloadFileIsSuccess,
    commentInProgress,
    accountsCategoriesLoading,
    documentIsLoading,
    documentIsFetching,
    debitAccountsListLoading,
    debitAccountsListUpdateInProgress,
    allDebitAccountBalancesIsLoading,
    allDebitAccountBalancesisFetching,
    allCreditAccountBalancesIsLoading,
    allCreditAccountBalancesisFetching,
    addDocumentInProgress,
    fileUploadIsSuccess,
    documentStatusChangeInProgress,
    documentCategoryChangeInProgress,
    editCommentInProgress,
    editCommentIsSuccess,
    deleteCommentInProgress,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    creditAccountsListLoading,
    creditAccountsListUpdateInProgress,
    deleteIsLoading,
    isLoading,
    internalError,
    getTransationDocumentsAction,
    linkedTransactionPath,
    onDeleteDocument,
    onMoveDocument,
    onChangeCustomerDocumentsStatus,
    onDownloadDocument,
    getTransactionChangeLogsAction,
    getDebitAccountsListAction,
    getCreditAccountsListAction,
    onSendDocumentComment,
    onEditComment,
    onDeleteComment,
    onUploadDocument,
    getTransactionDocumentByIdAction,
    handleDelete,
    onSearch,
    onDelete,
    handleClose,
    debitAccounts,
    creditAccounts,
    onUpdateBalance,
    setDebitAccounts,
    setCreditAccounts,
  } = useRequests({
    transactionId,
    transaction,
    transactionsTypes,
    search,
    documents,
    creditAccountNumber,
    debitAccountNumber,
    document: documentItem,
    setError,
    currencyId,
    baseCurrency,
    panelIsOpen,
    prevPanelIsOpen,
    selectedTake,
    restatement,
    setValue,
    debitBalances,
    creditBalances,
    currencies,
    isDirty,
    fields,
    activePermissions,
  });

  const onSubmit = handleSubmit(async () => {
    if (!Object.keys(errors).length) {
      setErrorMessage('');
      const values = getValues();

      const date = new Date(values.transferDate);
      const currentDate = new Date();
      const hours = currentDate.getHours();
      const minutes = currentDate.getMinutes();
      const seconds = currentDate.getSeconds();

      date.setHours(hours);
      date.setMinutes(minutes);
      date.setSeconds(seconds);

      const body = {
        ...values,
        sum: Number(values.sum),
        documentNumber: values.documentNumber || undefined,
        transferDate: date.toISOString(),
        info: values.info || undefined,
      };

      const response = (await create(
        body as GeneralLedgerTransactionFormValues,
      )) as {
        data?: GeneralLedgerTransactionType;
        error: ResponseError;
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      }
    }
  });

  useEffect(() => {
    dispatch(changePanelState({ state: true, transactionId }));
  }, [dispatch, currencies]);

  useEffect(() => {
    if (transferDateValue) {
      const transferDateFormatted = formatFormDate(transferDateValue);
      const currentDate = formatFormDate(new Date().toISOString());

      if (transferDateValue && transferDateFormatted > currentDate) {
        setError('transferDate', {
          type: 'custom',
          message: 'Creation Date cannot be later than current date',
        });
      } else {
        clearErrors('transferDate');
      }
    }
  }, [transferDateValue]);

  return (
    <>
      <Popup
        title="Delete transaction?"
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={handleDelete}
        cancelButtonText="Cancel"
        okButtonText={
          createIsLoading || deleteIsLoading ? 'Loading...' : 'Delete'
        }
        isVisible={deletePopupVisible}
        parent={document.getElementById('delete-popup') as HTMLElement}
      >
        <div>
          {deleteError && (
            <div className={cn.transactionDeleteError}>{deleteError}</div>
          )}
        </div>
      </Popup>
      <GeneralLedgerSidepanel
        sidepanelExpandedKey="gl-transaction-panel"
        panelIsOpen={panelIsOpen}
        handleClose={handleClose}
        title={transactionId ? 'GL Transfer' : 'New GL Transfer'}
        transaction={transaction}
        isLoading={isLoading || Boolean(!transaction && transactionId)}
        onDelete={onDelete}
        control={transactionId ? undefined : (control as Control<any>)}
        onSubmit={onSubmit}
        currencies={availableCurrencies}
        createIsLoading={createIsLoading}
        debitAccounts={debitAccounts}
        creditAccounts={creditAccounts}
        debitIsLoading={
          debitAccountsListLoading || debitAccountsListUpdateInProgress
        }
        creditIsLoading={
          creditAccountsListLoading || creditAccountsListUpdateInProgress
        }
        debitBalanceIsLoading={debitBalanceIsLoading || debitBalanceIsFetching}
        creditBalanceIsLoading={
          creditBalanceIsLoading || creditBalanceIsFetching
        }
        onUpdateBalance={onUpdateBalance}
        debitAccountBalance={debitAccountBalance}
        creditAccountBalance={creditAccountBalance}
        isCreateForm={!transactionId}
        internalError={internalError}
        createAllowed={activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_CREATE_ACTION,
        )}
        isEditable={
          transactionId
            ? undefined
            : Boolean(
                activePermissions?.includes(
                  PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_CREATE_ACTION,
                ),
              )
        }
        onSearchAutocomplete={onSearch}
        errors={errors && Object.keys(errors).length ? errors : undefined}
        accountCategories={accountCategories}
        accountsCategoriesLoading={accountsCategoriesLoading}
        setDebitAccounts={setDebitAccounts}
        setCreditAccounts={setCreditAccounts}
        setDebitAccountQuery={getDebitAccountsListAction}
        setCreditAccountQuery={getCreditAccountsListAction}
        linkedTransactionPath={linkedTransactionPath}
        backPath={`${pathname}${search || ''}`}
        isDirty={isDirty}
        errorSidepanel={errorMessage}
        debitBalances={debitBalances}
        creditBalances={creditBalances}
        allDebitAccountIsLoading={
          allDebitAccountBalancesIsLoading || allDebitAccountBalancesisFetching
        }
        allCreditAccountIsLoading={
          allCreditAccountBalancesIsLoading ||
          allCreditAccountBalancesisFetching
        }
        currenciesList={dictionariesCurrenciesList}
        currencyId={currencyId}
        overdraftEnabled={overdraftEnabled}
        debitAccountNumber={debitAccountNumber}
        creditAccountNumber={creditAccountNumber}
        baseCurrency={baseCurrency}
        activePermission={activePermissions}
        users={users}
        changeLogsIsLoading={changeLogsIsLoading}
        changeLogsIsFetching={changeLogsIsFetching}
        getTransactionChangeLogs={getTransactionChangeLogsAction}
        transactionChangeLogsPagination={transactionChangeLogsPagination}
        changeLogs={transactionChangeLogs}
        getTransationDocumentsAction={getTransationDocumentsAction}
        documentsIsLoading={documentsIsLoading}
        documentsIsFetching={documentsIsFetching}
        documents={documents}
        documentsPagination={documentsPagination}
        attachedDocumentsErrors={attachedDocumentsErrors}
        uploadedDocumentsErrors={uploadedDocumentsErrors}
        uploadDocumentErrors={uploadedFilesError}
        uploadDocumentInProgress={
          checkDocumentNameInProgress ||
          fileUploadInProgress ||
          addDocumentInProgress
        }
        uploadDocumentIsSuccess={fileUploadIsSuccess}
        onUploadDocument={onUploadDocument}
        onDownloadDocument={onDownloadDocument}
        onDownloadSuccess={downloadFileIsSuccess}
        downloadInProgress={downloadInProgress || downloadIsFetching}
        documentFileIsLoading={downloadInProgress || downloadIsFetching}
        documentItem={documentItem}
        documentIsLoading={documentIsLoading || documentIsFetching}
        imgLink={imgLink}
        getTransactionDocument={getTransactionDocumentByIdAction}
        documentStatusChangeInProgress={documentStatusChangeInProgress}
        changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
        onMove={onMoveDocument}
        moveInProgress={documentCategoryChangeInProgress}
        deleteIsVisible={activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DOCUMENTS_DELETE_ACTION,
        )}
        commentInProgress={commentInProgress}
        sendComment={onSendDocumentComment}
        editCommentInProgress={editCommentInProgress}
        editCommentIsSuccess={editCommentIsSuccess}
        editComment={onEditComment}
        deleteCommentInProgress={deleteCommentInProgress}
        deleteComment={onDeleteComment}
        onDeleteDocument={onDeleteDocument}
        deleteInProgress={deleteDocumentInProgress}
        deleteIsSuccess={deleteDocumentIsSuccess}
        deleteDisabled={ffSettings?.ffDisableTxGlRemoval}
      />
    </>
  );
};

export default GeneralLedgerTransaction;
